import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api/api';
import config from '../config/environment';
import './AuditLogs.css';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchAuditLogs();
    fetchSecurityEvents();
  }, [page, filter]);

  const fetchAuditLogs = async () => {
    try {
      setError(null);

      const params = { page, size: 20 };
      
      if (filter !== 'all') {
        params.actionType = filter;
      }

      const response = await axios.get(`${config.apiGatewayUrl}/api/audit/logs`, {
        params,
        timeout: 10000 // 10 second timeout
      });

      // Validate response data
      if (response.data && Array.isArray(response.data.logs)) {
        setLogs(response.data.logs);
        setTotalPages(response.data.totalPages || 0);
      } else {
        setLogs([]);
        setTotalPages(0);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch audit logs');
      setLogs([]);
      setTotalPages(0);
      setLoading(false);
    }
  };

  const fetchSecurityEvents = async () => {
    try {
      const response = await axios.get(`${config.apiGatewayUrl}/api/audit/security-events`, {
        params: { hours: 24 },
        timeout: 10000 // 10 second timeout
      });

      // Validate response data
      if (response.data && Array.isArray(response.data)) {
        setSecurityEvents(response.data);
      } else {
        setSecurityEvents([]);
      }
    } catch (error) {
      console.error('Error fetching security events:', error);
      setSecurityEvents([]);
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getActionTypeColor = (actionType) => {
    const securityActions = ['RATE_LIMIT_EXCEEDED', 'INVALID_TOKEN', 'UNAUTHORIZED_ACCESS', 'SUSPICIOUS_ACTIVITY', 'IP_BLOCKED'];
    if (securityActions.includes(actionType)) return 'red';
    if (actionType.includes('LOGIN') || actionType.includes('SIGNUP')) return 'blue';
    if (actionType.includes('PAYMENT')) return 'green';
    return 'gray';
  };

  if (loading) {
    return (
      <div className="audit-logs-container">
        <div className="loading-spinner">
          <p>Loading audit logs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="audit-logs-container">
        <div className="error-message">
          <h3>⚠️ Error Loading Audit Logs</h3>
          <p>{error}</p>
          <button onClick={() => { setError(null); setLoading(true); fetchAuditLogs(); }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-logs-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0 }}>Audit Logs & Security Monitoring</h2>
        <button onClick={() => window.location.href = '/admin'}
          style={{ padding: '10px 20px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
          ← Back to Dashboard
        </button>
      </div>

      {securityEvents.length > 0 && (
        <div className="security-alert">
          <h3>⚠️ Security Events (Last 24 Hours)</h3>
          <p className="alert-count">{securityEvents.length} security events detected</p>
          <div className="security-events-list">
            {securityEvents.slice(0, 5).map((event, index) => (
              <div key={index} className="security-event-item">
                <span className="event-time">{formatDate(event.createdAt)}</span>
                <span className="event-type">{event.actionType}</span>
                <span className="event-desc">{event.description}</span>
                <span className="event-ip">{event.ipAddress}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="filter-section">
        <label>Filter by Action Type:</label>
        <select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }}>
          <option value="all">All Actions</option>
          <option value="LOGIN">Login</option>
          <option value="SIGNUP">Signup</option>
          <option value="PAYMENT_CREATE">Payment Create</option>
          <option value="PAYMENT_UPDATE">Payment Update</option>
          <option value="PAYMENT_REFUND">Payment Refund</option>
          <option value="SUSPICIOUS_ACTIVITY">Suspicious Activity</option>
          <option value="RATE_LIMIT_EXCEEDED">Rate Limit Exceeded</option>
        </select>
      </div>

      <div className="audit-logs-table">
        <table role="table">
          <caption className="sr-only">Audit Logs</caption>
          <thead>
            <tr>
              <th scope="col">Time</th>
              <th scope="col">User</th>
              <th scope="col">Action</th>
              <th scope="col">Entity</th>
              <th scope="col">Description</th>
              <th scope="col">IP Address</th>
              <th scope="col">Service</th>
            </tr>
          </thead>
          <tbody>
            {logs && logs.length > 0 ? (
              logs.map((log) => (
                <tr key={log.id || Math.random()}>
                  <td>{formatDate(log.createdAt)}</td>
                  <td>{log.username || log.userId || '-'}</td>
                  <td>
                    <span 
                      className="action-badge" 
                      style={{ backgroundColor: getActionTypeColor(log.actionType || 'UNKNOWN') }}
                    >
                      {log.actionType || 'UNKNOWN'}
                    </span>
                  </td>
                  <td>{log.entityType || '-'} {log.entityId && `(${log.entityId})`}</td>
                  <td className="description">{log.description || '-'}</td>
                  <td>{log.ipAddress || '-'}</td>
                  <td>{log.serviceName || '-'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                  No audit logs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button 
          onClick={() => setPage(page - 1)} 
          disabled={page === 0}
        >
          Previous
        </button>
        <span>Page {page + 1} of {totalPages}</span>
        <button 
          onClick={() => setPage(page + 1)} 
          disabled={page >= totalPages - 1}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AuditLogs;
